import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { AppHeader } from "@/components/AppHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Phone, Clock, Calendar, ArrowLeft, Archive, CheckCircle2, Play, Pause } from "lucide-react";
import { toast } from "sonner";

const MessageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [message, setMessage] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  
  useEffect(() => {
    // Mock message data - in production, fetch from API
    const mockMessages = {
      "1": {
        id: "1",
        caller: "06 12 34 56 78",
        date: new Date(Date.now() - 2 * 60 * 60 * 1000),
        duration: "1:23",
        isNew: true,
        transcription: "Bonjour docteur, c'est Madame Leblanc à l'appareil. Je souhaiterais prendre rendez-vous pour un contrôle de routine. J'ai des disponibilités en fin de semaine, jeudi ou vendredi après-midi si possible. J'ai également besoin de renouveler mon ordonnance pour mon traitement habituel. Pouvez-vous me rappeler quand vous aurez un moment ? Merci beaucoup. Bonne journée.",
        audioUrl: null
      },
      "2": {
        id: "2",
        caller: "06 98 76 54 32",
        date: new Date(Date.now() - 5 * 60 * 60 * 1000),
        duration: "0:45",
        isNew: true,
        transcription: "Bonjour, c'est Madame Martin. Je vous appelle pour renouveler mon ordonnance habituelle pour mon traitement de l'hypertension. Je n'ai plus que pour quelques jours. Est-il possible de passer la chercher demain à votre cabinet ? Merci d'avance.",
        audioUrl: null
      },
      "3": {
        id: "3",
        caller: "07 11 22 33 44",
        date: new Date(Date.now() - 24 * 60 * 60 * 1000),
        duration: "2:10",
        isNew: false,
        transcription: "Bonjour docteur, je suis le père du petit Thomas, votre patient. Il a de la fièvre depuis hier soir, autour de 38,5 degrés, et il se plaint de maux de gorge. Il a également le nez qui coule. Je lui ai donné du paracétamol mais je voulais avoir votre avis. Faut-il prendre rendez-vous rapidement ou est-ce que je peux attendre quelques jours ? Il n'a pas de difficultés à respirer et il continue à jouer normalement. Merci de me rappeler dès que possible.",
        audioUrl: null
      },
      "4": {
        id: "4",
        caller: "06 55 66 77 88",
        date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
        duration: "1:05",
        isNew: false,
        transcription: "Bonjour docteur, c'est Monsieur Durand. Je voulais vous informer que les analyses que vous m'aviez prescrites lors de ma dernière consultation sont maintenant disponibles. Je peux passer les récupérer à tout moment. Souhaitez-vous que je prenne rendez-vous pour en discuter avec vous ? Merci.",
        audioUrl: null
      },
      "5": {
        id: "5",
        caller: "06 44 33 22 11",
        date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
        duration: "0:32",
        isNew: false,
        transcription: "Bonjour, c'est pour annuler mon rendez-vous de jeudi prochain à 14 heures. J'ai un empêchement professionnel. Est-il possible de reporter à la semaine suivante ? Merci de me rappeler pour confirmer.",
        audioUrl: null
      }
    };
    
    const fetchedMessage = mockMessages[id];
    if (fetchedMessage) {
      setMessage(fetchedMessage);
      // Mark as read
      if (fetchedMessage.isNew) {
        setTimeout(() => {
          setMessage(prev => ({ ...prev, isNew: false }));
        }, 1000);
      }
    }
  }, [id]);
  
  const handleMarkAsRead = () => {
    setMessage(prev => ({ ...prev, isNew: false }));
    toast.success("Message marqué comme lu");
  };
  
  const handleArchive = () => {
    toast.success("Message archivé");
    navigate("/app/messages");
  };
  
  const togglePlayback = () => {
    if (message.audioUrl) {
      setIsPlaying(!isPlaying);
      // In production, actually play/pause audio
      toast.info(isPlaying ? "Lecture en pause" : "Lecture en cours");
    } else {
      toast.info("Enregistrement audio non disponible");
    }
  };
  
  if (!message) {
    return (
      <div className="flex min-h-screen flex-col bg-muted/20">
        <AppHeader />
        <main className="flex flex-1 items-center justify-center">
          <p className="text-muted-foreground">Message introuvable</p>
        </main>
      </div>
    );
  }
  
  return (
    <div className="flex min-h-screen flex-col bg-muted/20">
      <AppHeader />
      
      <main className="flex-1">
        <div className="container-custom py-8">
          {/* Back Button */}
          <Link to="/app/messages">
            <Button variant="ghost" className="mb-6 gap-2">
              <ArrowLeft className="h-4 w-4" />
              Retour aux messages
            </Button>
          </Link>
          
          {/* Message Card */}
          <Card className="border-2">
            <CardContent className="p-8">
              {/* Header */}
              <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-bold">Détail du message</h1>
                    {message.isNew && (
                      <Badge className="bg-accent text-accent-foreground">Nouveau</Badge>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      <span className="font-medium">{message.caller}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>{message.date.toLocaleDateString('fr-FR', { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>Durée : {message.duration}</span>
                    </div>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex gap-2">
                  {message.isNew && (
                    <Button variant="outline" onClick={handleMarkAsRead} className="gap-2">
                      <CheckCircle2 className="h-4 w-4" />
                      Marquer comme lu
                    </Button>
                  )}
                  <Button variant="outline" onClick={handleArchive} className="gap-2">
                    <Archive className="h-4 w-4" />
                    Archiver
                  </Button>
                </div>
              </div>
              
              {/* Audio Player */}
              {message.audioUrl && (
                <Card className="mb-6 bg-muted/50">
                  <CardContent className="flex items-center gap-4 p-6">
                    <Button
                      size="lg"
                      variant="outline"
                      onClick={togglePlayback}
                      className="gap-2"
                    >
                      {isPlaying ? (
                        <>
                          <Pause className="h-5 w-5" />
                          Pause
                        </>
                      ) : (
                        <>
                          <Play className="h-5 w-5" />
                          Écouter
                        </>
                      )}
                    </Button>
                    <div className="flex-1">
                      <div className="h-2 rounded-full bg-muted">
                        <div className="h-full w-0 rounded-full bg-primary transition-all" />
                      </div>
                    </div>
                    <span className="text-sm text-muted-foreground">{message.duration}</span>
                  </CardContent>
                </Card>
              )}
              
              {/* Transcription */}
              <div className="space-y-3">
                <h2 className="text-lg font-semibold">Transcription</h2>
                <Card className="bg-background">
                  <CardContent className="p-6">
                    <p className="leading-relaxed text-foreground whitespace-pre-wrap">
                      {message.transcription}
                    </p>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default MessageDetail;